import dayjs from 'dayjs';
import React from 'react';
import { approvalMap, confidenceThreshold, Routes } from '~/config';
import { type RouterOutputs } from '~/utils/api';
import { getIsApproved } from '~/utils/trademark';

import { LinkIcon } from '@heroicons/react/24/outline';
import { ClaimAndTrademarkApproval } from '@prisma/client';

import Button from '../Button';
import Chip from '../Chip';
import Tooltip from '../Tooltip';
import { cn } from '~/lib/utils';
import { useWorkspace } from '~/contexts/WorkspaceContext';

type SimilarUsagesProps = {
  similarUsages: RouterOutputs['feedback']['getFeedbackSuggestionsAndSimilarUsages']['results']['claims'][0]['similarUsages'];
  type: 'claim' | 'policy-feedback';
  documentText?: string;
  isCompactView?: boolean;
};

export default function SimilarUsages(props: SimilarUsagesProps) {
  const [isShowMore, setIsShowMore] = React.useState(false);
  const { workspaceId } = useWorkspace();
  if (!props.similarUsages?.length) {
    return null;
  }

  const visible = props.similarUsages
    ?.slice()
    .sort((a, b) => b.confidence - a.confidence)
    .filter((x) => isShowMore || x.confidence >= 0.75)
    .slice(0, isShowMore ? props.similarUsages.length : 2);

  return (
    <div className="space-y-2">
      {!!props.similarUsages?.length && (
        <>
          <div className="flex items-center justify-between gap-2">
            <p className={cn('text-foreground font-medium', props.isCompactView ? 'text-xs' : 'text-[13px]')}>
              Similar {props.type === 'claim' ? 'claims' : 'usages'}
            </p>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={(e) => {
                e.stopPropagation();
                setIsShowMore((prev) => !prev);
              }}
            >
              {isShowMore ? 'Show less' : 'Show more'}
            </Button>
          </div>
          <div className="space-y-1.5">
            {visible.map((c) => {
              const isHighConfidence = c.confidence >= confidenceThreshold.high;
              const link = c.document.task?.remoteTaskManager
                ? `/${workspaceId}${Routes.PROJECTS}/${c.document.task.remoteTaskManager.id}/list?taskId=${c.document.taskId}`
                : `/${workspaceId}${Routes.DECISION_STORE}/${props.type}/${c.id}`;

              const disclosure =
                getIsApproved(c.approval) && c.disclosureText
                  ? {
                      text: c.disclosureText,
                      isMissing: !props.documentText
                        ?.toLowerCase()
                        .includes(c.disclosureText.toLowerCase()),
                    }
                  : undefined;

              return (
                <a
                  key={c.id}
                  href={link}
                  target="_blank"
                  rel="noreferrer"
                  className={cn(
                    'border-border hover:bg-accent/30 block rounded-lg border px-2.5 py-2',
                    props.isCompactView ? 'text-xs' : 'text-sm',
                  )}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Tooltip
                    content={
                      isHighConfidence
                        ? `The current text's claim is a close match with this historical statement.`
                        : `The current text's claim is somewhat similar with this historical statement.`
                    }
                    hideIcon
                  >
                    <p className="text-foreground text-left font-medium">{c.text}</p>
                  </Tooltip>
                  <div className="text-muted-foreground mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px]">
                    <Chip
                      text={isHighConfidence ? 'High' : 'Low'}
                      level={isHighConfidence ? 'alert' : 'warning'}
                    />
                    {disclosure && (
                      <Tooltip
                        content={
                          <div>
                            This text was approved with the required disclosure:{' '}
                            <span className="font-semibold">{disclosure.text}</span>
                          </div>
                        }
                        anchorClassName="w-fit"
                        hideIcon
                      >
                        <Chip
                          text={`${disclosure.isMissing ? 'Missing' : 'Present'} disclosure`}
                          level={disclosure.isMissing ? 'alert' : 'success'}
                        />
                      </Tooltip>
                    )}
                    <span
                      className="whitespace-nowrap"
                      title={
                        ClaimAndTrademarkApproval.LIMITED_APPROVAL === c.approval
                          ? c.approvalLimitations ?? undefined
                          : undefined
                      }
                    >
                      {approvalMap[c.approval ?? ClaimAndTrademarkApproval.LIMITED_APPROVAL]}
                    </span>
                    <span className="ml-auto flex items-center gap-1 whitespace-nowrap">
                      {dayjs(c.createdAt).format('MMM D, YYYY')}
                      <LinkIcon className="size-3.5" strokeWidth={2} />
                    </span>
                  </div>
                </a>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
